import React, { useState, useEffect } from 'react';
import { FileText, Search, Filter, Download, RefreshCw, User, Calendar, Activity } from 'lucide-react';
import { api } from '../api/client';

const AuditLogsPage = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: '',
    action: '',
    user: '',
    dateFrom: '',
    dateTo: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0
  });

  useEffect(() => {
    loadLogs();
  }, [pagination.page, filters]);

  const loadLogs = async () => {
    try {
      setLoading(true);
      const response = await api.getAuditLogs({
        limit: pagination.limit,
        skip: (pagination.page - 1) * pagination.limit,
        ...filters
      });
      setLogs(response.data.logs || []);
      setPagination(prev => ({ ...prev, total: response.data.total || 0 }));
    } catch (error) {
      console.error('Failed to load audit logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    // TODO: Implement CSV export
    alert('Export functionality coming soon!');
  };

  const getActionColor = (action) => {
    const colors = {
      'CREATE': 'bg-green-100 text-green-700',
      'UPDATE': 'bg-blue-100 text-blue-700',
      'DELETE': 'bg-red-100 text-red-700',
      'LOGIN': 'bg-purple-100 text-purple-700',
      'LOGOUT': 'bg-[var(--clr-surface-subtle)] text-gray-700',
      'UPLOAD': 'bg-amber-100 text-amber-700',
      'DOWNLOAD': 'bg-teal-100 text-teal-700'
    };
    return colors[action?.toUpperCase()] || 'bg-[var(--clr-surface-subtle)] text-gray-700';
  };

  const actionTypes = ['CREATE', 'UPDATE', 'DELETE', 'LOGIN', 'LOGOUT', 'UPLOAD', 'DOWNLOAD'];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[var(--clr-text-primary)] flex items-center gap-3">
            <FileText className="w-7 h-7 text-[var(--clr-sev-atrisk)]" />
            Audit Logs
          </h1>
          <p className="text-[var(--clr-text-secondary)] mt-1">Track all user actions and system events</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={loadLogs}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-[var(--clr-surface-hover)] flex items-center gap-2"
            data-testid="refresh-logs-btn"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={handleExport}
            className="px-4 py-2 bg-[var(--clr-brand-primary)] text-white rounded-lg hover:bg-[var(--clr-brand-primary-hover)] flex items-center gap-2"
            data-testid="export-logs-btn"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-[var(--clr-surface-card)] rounded-xl border shadow-[var(--shadow-card)] p-4">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-5 h-5 text-[var(--clr-text-muted)]" />
          <span className="font-medium text-gray-700">Filters</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[var(--clr-text-muted)]" />
            <input
              type="text"
              placeholder="Search logs..."
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-[var(--clr-brand-primary)]"
              data-testid="search-logs-input"
            />
          </div>
          <select
            value={filters.action}
            onChange={(e) => setFilters(prev => ({ ...prev, action: e.target.value }))}
            className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[var(--clr-brand-primary)]"
            data-testid="action-filter-select"
          >
            <option value="">All Actions</option>
            {actionTypes.map(action => (
              <option key={action} value={action}>{action}</option>
            ))}
          </select>
          <input
            type="text"
            placeholder="User name..."
            value={filters.user}
            onChange={(e) => setFilters(prev => ({ ...prev, user: e.target.value }))}
            className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[var(--clr-brand-primary)]"
            data-testid="user-filter-input"
          />
          <input
            type="date"
            value={filters.dateFrom}
            onChange={(e) => setFilters(prev => ({ ...prev, dateFrom: e.target.value }))}
            className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[var(--clr-brand-primary)]"
            data-testid="date-from-input"
          />
          <input
            type="date"
            value={filters.dateTo}
            onChange={(e) => setFilters(prev => ({ ...prev, dateTo: e.target.value }))}
            className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[var(--clr-brand-primary)]"
            data-testid="date-to-input"
          />
        </div>
      </div>

      {/* Logs Table */}
      <div className="bg-[var(--clr-surface-card)] rounded-xl border shadow-[var(--shadow-card)] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[var(--clr-surface-subtle)] border-b">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-[var(--clr-text-secondary)]">Timestamp</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-[var(--clr-text-secondary)]">User</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-[var(--clr-text-secondary)]">Action</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-[var(--clr-text-secondary)]">Resource</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-[var(--clr-text-secondary)]">Details</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-[var(--clr-text-secondary)]">IP Address</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center">
                    <RefreshCw className="w-8 h-8 text-[var(--clr-text-muted)] animate-spin mx-auto mb-2" />
                    <p className="text-[var(--clr-text-muted)]">Loading logs...</p>
                  </td>
                </tr>
              ) : logs.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center">
                    <Activity className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                    <p className="text-[var(--clr-text-muted)]">No audit logs found</p>
                    <p className="text-sm text-[var(--clr-text-muted)] mt-1">Activity will appear here as users interact with the system</p>
                  </td>
                </tr>
              ) : (
                logs.map((log, idx) => (
                  <tr key={log._id || idx} className="hover:bg-[var(--clr-surface-hover)]" data-testid={`audit-log-row-${idx}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-[var(--clr-text-muted)]" />
                        <span className="text-sm text-[var(--clr-text-primary)]">
                          {new Date(log.timestamp).toLocaleDateString()}
                        </span>
                        <span className="text-xs text-[var(--clr-text-muted)]">
                          {new Date(log.timestamp).toLocaleTimeString()}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-gray-200 flex items-center justify-center">
                          <User className="w-4 h-4 text-[var(--clr-text-muted)]" />
                        </div>
                        <span className="text-sm text-[var(--clr-text-primary)]">{log.user_name || 'System'}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getActionColor(log.action)}`}>
                        {log.action}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-[var(--clr-text-primary)]">{log.resource_type}</span>
                      {log.resource_id && (
                        <span className="text-xs text-[var(--clr-text-muted)] ml-1">({log.resource_id.slice(-6)})</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-[var(--clr-text-secondary)] truncate max-w-xs block">
                        {log.details ? (typeof log.details === 'object' ? JSON.stringify(log.details) : log.details) : '-'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-[var(--clr-text-muted)] font-mono">{log.ip_address || '-'}</span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.total > pagination.limit && (
          <div className="px-4 py-3 border-t flex items-center justify-between">
            <p className="text-sm text-[var(--clr-text-secondary)]">
              Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} logs
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                disabled={pagination.page === 1}
                className="px-3 py-1 border rounded hover:bg-[var(--clr-surface-hover)] disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="prev-page-btn"
              >
                Previous
              </button>
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                disabled={pagination.page * pagination.limit >= pagination.total}
                className="px-3 py-1 border rounded hover:bg-[var(--clr-surface-hover)] disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="next-page-btn"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-[var(--clr-surface-card)] rounded-xl border p-4 shadow-[var(--shadow-card)]">
          <p className="text-sm text-[var(--clr-text-secondary)]">Total Events</p>
          <p className="text-2xl font-bold text-[var(--clr-text-primary)]">{pagination.total}</p>
        </div>
        <div className="bg-[var(--clr-surface-card)] rounded-xl border p-4 shadow-[var(--shadow-card)]">
          <p className="text-sm text-[var(--clr-text-secondary)]">Create Actions</p>
          <p className="text-2xl font-bold text-[var(--clr-sev-healthy)]">
            {logs.filter(l => l.action?.toUpperCase() === 'CREATE').length}
          </p>
        </div>
        <div className="bg-[var(--clr-surface-card)] rounded-xl border p-4 shadow-[var(--shadow-card)]">
          <p className="text-sm text-[var(--clr-text-secondary)]">Update Actions</p>
          <p className="text-2xl font-bold text-[var(--clr-brand-primary)]">
            {logs.filter(l => l.action?.toUpperCase() === 'UPDATE').length}
          </p>
        </div>
        <div className="bg-[var(--clr-surface-card)] rounded-xl border p-4 shadow-[var(--shadow-card)]">
          <p className="text-sm text-[var(--clr-text-secondary)]">Delete Actions</p>
          <p className="text-2xl font-bold text-[var(--clr-sev-blocked)]">
            {logs.filter(l => l.action?.toUpperCase() === 'DELETE').length}
          </p>
        </div>
      </div>
    </div>
  );
};

export default AuditLogsPage;
